import { type ReactNode } from 'react';
import { motion } from 'framer-motion';

interface SectionProps {
  id: string;
  children: ReactNode;
  className?: string;
  dark?: boolean;
  pattern?: 'none' | 'grid' | 'hazard' | 'diagonal';
  innerClassName?: string;
}

export function Section({
  id,
  children,
  className = '',
  dark = false,
  pattern = 'none',
  innerClassName = '',
}: SectionProps) {
  const getPatternClasses = () => {
    switch (pattern) {
      case 'grid':
        return 'blueprint-bg';
      case 'hazard':
        return 'hazard-bg';
      case 'diagonal':
        return 'bg-diagonal-lines';
      default:
        return '';
    }
  };

  return (
    <section
      id={id}
      className={`relative overflow-hidden ${dark ? 'bg-surface-dark text-white' : 'bg-white'} ${getPatternClasses()} ${className}`}
    >
      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 ${innerClassName}`}>
        {children}
      </div>
    </section>
  );
}

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  align?: 'left' | 'center';
  dark?: boolean;
}

export function SectionHeader({
  title,
  subtitle,
  align = 'left',
  dark = false,
}: SectionHeaderProps) {
  return (
    <motion.div
      className={`mb-12 ${align === 'center' ? 'text-center' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.5 }}
    >
      {/* Section Label */}
      <div className={`flex items-center gap-4 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <div className="w-12 h-1 bg-hul-red" />
        <span className={`font-mono text-sm uppercase tracking-wider ${dark ? 'text-white/70' : 'text-hul-red'}`}>
          {subtitle || 'Proposal Details'}
        </span>
        <div className="w-12 h-1 bg-hul-red" />
      </div>

      {/* Main Title */}
      <h2
        className={`font-display text-4xl lg:text-5xl tracking-wide ${
          dark ? 'text-white' : 'text-hul-navy'
        }`}
      >
        {title}
      </h2>

      {/* Decorative Line */}
      {align === 'left' && (
        <div className="mt-4 flex items-center gap-2">
          <div className="w-24 h-0.5 bg-gradient-to-r from-hul-red to-transparent" />
          <div className="w-2 h-2 bg-hul-red transform rotate-45" />
        </div>
      )}
    </motion.div>
  );
}

interface SectionDividerProps {
  variant?: 'default' | 'thin' | 'accent';
}

export function SectionDivider({ variant = 'default' }: SectionDividerProps) {
  if (variant === 'thin') {
    return <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />;
  }

  if (variant === 'accent') {
    return (
      <div className="relative h-2">
        <div className="absolute inset-0 bg-gradient-to-r from-hul-red via-hul-red-light to-hul-red" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `repeating-linear-gradient(
              90deg,
              transparent,
              transparent 20px,
              rgba(0, 0, 0, 0.2) 20px,
              rgba(0, 0, 0, 0.2) 22px
            )`,
          }}
        />
      </div>
    );
  }

  return <div className="industrial-divider" />;
}
